import type { Building } from "building-types/building/buildingTypes";
import type { StreetOrientationMode } from "building-types/building/streetOrientationMode";
import type { GeneratedRoof } from "building-types/roofs/generatedRoofSystemTypes";
import type { MultiGableRoofClipContext } from "building-types/roofs/multiGableRoofClipContextTypes";
import type { RoofPerimeterRail } from "building-types/roofs/roofPerimeterTypes";
import type { Room } from "building-types/rooms/roomTypes";
import { RoomClass } from "building-types/rooms/roomTypesEnum";
import type { WallSettings } from "building-types/settings/wallSettings";
import { WallType } from "building-types/walls/wallEnums";
import { WallLayerType } from "building-types/walls/wallEnums";
import type { WallSystem } from "building-types/walls/wallSytemTypes";
import type { Table } from "common/data/table";
import { isInteriorRoomEnclosure } from "doc/rooms/roomAttributesTools";
import { MULTIGABLE_BSP_EPSILON } from "doc/utils/roofTools";
import { isExteriorWallCondition } from "doc/utils/wallTypeTools";
import { aabb2Width } from "geom/aabb2Types";
import { aabb2Height } from "geom/aabb2Types";
import type { Face } from "geom/brep";
import { addOutsidePolygonsToBSP, buildEdgePolygon, buildPolygonsFromPolyline2 } from "geom/bsp";
import type { ClipRailContext } from "geom/bspClipping";
import { clippingNodeNew, clipRail3, clipRails3 } from "geom/bspClipping";
import type { NodeBSP, PolygonBSP } from "geom/bspTypes";
import { DIST_EPSILON } from "geom/core";
import { offsetPolyLine2 } from "geom/offset";
import {
  planeByThreePoints,
  planeCopy,
  planeCopyReversed,
  planeMoveAlongNormal,
  planeNormal,
  planeReProjectPolyLine2,
  polyLine2OnPlane,
} from "geom/plane";
import { polyLine2AABB2, polyLine2End, polyLine2Start } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import { polyLine3ByTwoPoints, polyLine3Move } from "geom/polyLine3";
import { getSoffitDepth } from "../../elements/roofTrimUtility";
import { getRoofEdgeWallOffsetWithLayer } from "../multigableRoofPerimeter";
import { includeElementBasedOnOrientation } from "../multiGableRoofTools";
import { getWallPath } from "../walls/wall";
import { solidWallSubstrateLayer } from "../walls/wallLayers";
import type { CoplanarRails } from "./roofRails";
import { coplanarRailsNew, groupCoplanarRails, rejoinCoplanarRails } from "./roofRails";

interface BuildExtraFriezeBoarRailsOptions {
  readonly subRoof: GeneratedRoof;
  readonly walls: Building["walls"];
  readonly friezeBoardOrientation: StreetOrientationMode;
  readonly clipContext: MultiGableRoofClipContext;
}

export function buildExtraFriezeBoardRails({
  subRoof,
  walls,
  friezeBoardOrientation,
  clipContext,
}: BuildExtraFriezeBoarRailsOptions): CoplanarRails[] {
  const { roofVolume, roofSettings } = subRoof;
  const { discardFriezeBSP, baseHeight } = roofVolume;
  const { includeFriezeBoardOnPorches } = roofSettings;

  const friezeRailOptions1: ClipRailContext = {
    clippingNodes: [clippingNodeNew(discardFriezeBSP, true)],
    coplanarsToFront: false,
    epsilon: MULTIGABLE_BSP_EPSILON,
  };

  const friezeRailOptions2: ClipRailContext = {
    clippingNodes: [clippingNodeNew(clipContext.insideBSP, true)],
    coplanarsToFront: true,
    epsilon: MULTIGABLE_BSP_EPSILON,
  };

  const friezeRails: CoplanarRails[] = [];

  const isValidRoom = (room: Room | undefined): boolean => {
    if (includeFriezeBoardOnPorches || !room) {
      return true;
    }

    return room.attributes.roomClass !== RoomClass.PORCH;
  };

  const addWallEdge = (room: Room | undefined, edge: PolyLine2, floorElevation: number, ceilingHeight: number) => {
    if (!isValidRoom(room)) {
      return;
    }

    const railHeight = Math.min(floorElevation + ceilingHeight, baseHeight - getSoffitDepth(roofSettings));

    if (railHeight < floorElevation - DIST_EPSILON || railHeight > floorElevation + ceilingHeight + DIST_EPSILON) {
      return;
    }

    const [x0, y0] = polyLine2Start(edge);
    const [x1, y1] = polyLine2End(edge);
    const rail = polyLine3ByTwoPoints(x0, y0, railHeight, x1, y1, railHeight);
    const railPlane = planeByThreePoints(x0, y0, 0, x1, y1, 0, x1, y1, 10);

    if (!railPlane || !includeElementBasedOnOrientation(railPlane, friezeBoardOrientation)) {
      return;
    }

    const [nX, nY, nZ] = planeNormal(railPlane);

    let rails = clipRail3({
      rail,
      options: friezeRailOptions1,
    });

    rails = clipRails3({
      rails,
      options: friezeRailOptions2,
    });

    if (rails.length === 0) {
      return;
    }

    friezeRails.push(coplanarRailsNew(railPlane, nX, nY, nZ, rails));
  };

  for (const levelId in walls) {
    const { walls: levelWalls } = walls[levelId];

    for (const wallId in levelWalls) {
      const wall = levelWalls[wallId];

      if (wall.wallType !== WallType.SOLID) {
        continue;
      }

      if (!isExteriorWallCondition[wall.wallCondition]) {
        continue;
      }

      const { mass } = wall;
      const path = getWallPath(wall);
      const { leftEdge, rightEdge } = solidWallSubstrateLayer(wall);
      const { floorElevation, section } = mass;
      const { leftRoom, rightRoom } = path;

      if (leftRoom?.attributes.roomEnclosure && isInteriorRoomEnclosure[leftRoom?.attributes.roomEnclosure]) {
        addWallEdge(rightRoom, rightEdge, floorElevation, section.topOffset);
      }

      if (rightRoom?.attributes.roomEnclosure && isInteriorRoomEnclosure[rightRoom?.attributes.roomEnclosure]) {
        addWallEdge(leftRoom, leftEdge, floorElevation, section.topOffset);
      }
    }
  }

  // group the rails that share the same plane
  const coplanarFriezeRails = groupCoplanarRails(friezeRails);

  // join rails from the same group
  const coplanarJoinedFriezeRails = rejoinCoplanarRails({
    inputRails: coplanarFriezeRails,
    keepRailsDirection: true,
  });

  return coplanarJoinedFriezeRails;
}

export function clipFriezeBoardRailsOutsideWalls(wallOverlapBSP: NodeBSP, friezeRails: PolyLine3[]): PolyLine3[] {
  const offsetZ = -0.5;

  return clipRails3({
    rails: friezeRails.map((rail) => polyLine3Move(rail, 0, 0, offsetZ)),
    options: {
      clippingNodes: [clippingNodeNew(wallOverlapBSP, true)],
      epsilon: 10 * MULTIGABLE_BSP_EPSILON,
    },
  }).map((rail) => polyLine3Move(rail, 0, 0, -offsetZ));
}

function addWallFacesToFriezeBoardBSP(bsp: NodeBSP | null, faces: Face[]): NodeBSP | null {
  const tolerance = 3 * MULTIGABLE_BSP_EPSILON;
  const thickness = 1;

  const addPolygons = (polygons: PolygonBSP[]) => {
    bsp = addOutsidePolygonsToBSP({
      node: bsp,
      coplanarsToFront: true,
      polygons,
      epsilon: MULTIGABLE_BSP_EPSILON,
    });
  };

  const addFace = (face: Face) => {
    const { loops, plane } = face;

    if (loops.length === 0) {
      return;
    }

    const aabb = polyLine2AABB2(loops[0]);
    const width = aabb2Width(aabb);
    const height = aabb2Height(aabb);

    if (width < tolerance || height < tolerance) {
      return;
    }

    const loop = offsetPolyLine2(loops[0], MULTIGABLE_BSP_EPSILON);

    const offsetPlane = planeMoveAlongNormal(planeCopy(plane), thickness / 2);
    const [nX, nY, nZ] = planeNormal(offsetPlane);
    const pl3 = polyLine2OnPlane(offsetPlane, loop);

    const polygons: PolygonBSP[] = [];
    for (let i = 0, l = pl3.length - 3; i < l; i += 3) {
      const polygon = buildEdgePolygon(
        pl3[i],
        pl3[i + 1],
        pl3[i + 2],
        pl3[i + 3],
        pl3[i + 4],
        pl3[i + 5],
        -nX * thickness,
        -nY * thickness,
        -nZ * thickness,
      );

      if (!polygon) {
        return;
      }

      polygons.push(polygon);
    }

    const oppositePlane = planeMoveAlongNormal(planeCopyReversed(offsetPlane), thickness);

    buildPolygonsFromPolyline2(loop, offsetPlane, 0, polygons);

    buildPolygonsFromPolyline2(planeReProjectPolyLine2(loop, offsetPlane, oppositePlane), oppositePlane, 0, polygons);

    addPolygons(polygons);
  };

  for (const face of faces) {
    addFace(face);
  }

  return bsp;
}

export function buildFriezeBoardWallOverlapFromSubRoofs(
  subRoofs: Table<GeneratedRoof>,
  bsp: NodeBSP | null = null,
): NodeBSP | null {
  for (const subRoofId in subRoofs) {
    const { wallFaces, roofWallSystem } = subRoofs[subRoofId];
    if (roofWallSystem) {
      bsp = buildFriezeBoardWallOverlapFromWallSytem(roofWallSystem.wallSystem, bsp);
    } else {
      const { startWallFaces, endWallFaces } = wallFaces;

      bsp = addWallFacesToFriezeBoardBSP(bsp, startWallFaces.leftFaces);
      bsp = addWallFacesToFriezeBoardBSP(bsp, endWallFaces.leftFaces);
    }
  }

  return bsp;
}

export function buildFriezeBoardWallOverlapFromWallSytems(
  wallSystems: Table<WallSystem>,
  bsp: NodeBSP | null = null,
): NodeBSP | null {
  for (const wallSystem of Object.values(wallSystems)) {
    bsp = buildFriezeBoardWallOverlapFromWallSytem(wallSystem, bsp);
  }

  return bsp;
}

export function buildFriezeBoardWallOverlapFromWallSytem(
  wallSystem: WallSystem,
  bsp: NodeBSP | null = null,
): NodeBSP | null {
  const { walls } = wallSystem;
  for (const wallId in walls) {
    const wall = walls[wallId];

    if (wall.wallType !== WallType.SOLID) {
      continue;
    }

    if (!isExteriorWallCondition[wall.wallCondition]) {
      continue;
    }

    const { wallFaces } = wall;
    const path = getWallPath(wall);
    const { leftRoom, rightRoom } = path;
    const { rightFaces, leftFaces, startFaces, endFaces } = wallFaces;

    const isLeftSideInterior = leftRoom?.attributes.roomEnclosure
      ? isInteriorRoomEnclosure[leftRoom?.attributes.roomEnclosure]
      : undefined;
    const isRightSideInterior = rightRoom?.attributes.roomEnclosure
      ? isInteriorRoomEnclosure[rightRoom?.attributes.roomEnclosure]
      : undefined;

    if (isLeftSideInterior) {
      bsp = addWallFacesToFriezeBoardBSP(bsp, rightFaces);
    }

    if (isRightSideInterior) {
      bsp = addWallFacesToFriezeBoardBSP(bsp, leftFaces);
    }

    if (isLeftSideInterior || isRightSideInterior) {
      bsp = addWallFacesToFriezeBoardBSP(bsp, startFaces);
      bsp = addWallFacesToFriezeBoardBSP(bsp, endFaces);
    }
  }

  return bsp;
}

export function adjustRoofRailsExteriorFinishOffset(subRoof: GeneratedRoof, wallSettings: WallSettings): GeneratedRoof {
  const { roofPerimeterRails, roofWallSystem } = subRoof;

  if (!roofWallSystem) {
    return subRoof;
  }

  const { wallMassSystem, planarGraph } = roofWallSystem;

  const newRoofPerimterRails: RoofPerimeterRail[] = [];
  for (const roofPerimeterRail of roofPerimeterRails) {
    const { baseEdge } = roofPerimeterRail;
    const exteriorWallOffsetWithFinish = getRoofEdgeWallOffsetWithLayer({
      wallMassSystem,
      planarGraph: planarGraph.planarGraph,
      edge: baseEdge,
      wallSettings,
      wallLayerType: WallLayerType.FINISH,
    });

    newRoofPerimterRails.push({
      ...roofPerimeterRail,
      exteriorWallOffsetWithFinish,
    });
  }

  return {
    ...subRoof,
    roofPerimeterRails: newRoofPerimterRails,
  };
}
